import { useEffect, useState, ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import PageBreadcrumb from "../common/PageBreadCrumb";
import InputWithLabel from "../form/input/InputWithLabel";
import AdminPageShell from "../admin/AdminPageShell";
import AdminCard from "../admin/AdminCard";
import AdminFormActions from "../admin/AdminFormActions";
import { adminAlertErrorClass, adminFormGridClass, adminFormFullWidthClass } from "../admin/adminStyles";

type FieldConfig = {
  name: string;
  label: string;
  type?: "text" | "number" | "date" | "datetime-local" | "textarea" | "checkbox" | "select";
  required?: boolean;
  placeholder?: string;
  options?: { value: string; label: string }[];
};

type SimpleCrudFormProps<T extends Record<string, unknown>> = {
  title: string;
  apiPath: string;
  listRoute: string;
  fields: FieldConfig[];
  initialData: T;
};

export default function SimpleCrudForm<T extends Record<string, unknown>>({
  title,
  apiPath,
  listRoute,
  fields,
  initialData,
}: SimpleCrudFormProps<T>) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [formData, setFormData] = useState<T>(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const normalizeFieldValue = (field: FieldConfig, value: unknown) => {
    if (value === null || value === undefined) {
      return value;
    }

    if (field.type === "date") {
      return String(value).slice(0, 10);
    }

    if (field.type === "datetime-local") {
      return String(value).slice(0, 16);
    }

    return value;
  };

  useEffect(() => {
    if (isEdit && id) {
      axiosInstance
        .get(`${apiPath}/${id}`)
        .then((res) => {
          const data = res.data;
          const normalized = { ...initialData };
          for (const key of Object.keys(initialData)) {
            if (data[key] !== undefined) {
              const field = fields.find((item) => item.name === key);
              (normalized as Record<string, unknown>)[key] = field
                ? normalizeFieldValue(field, data[key])
                : data[key];
            }
          }
          setFormData(normalized);
        })
        .catch(() => setError("Failed to load"));
    }
  }, [apiPath, fields, id, initialData, isEdit]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? (e.target as HTMLInputElement).checked
          : type === "number"
            ? value === ""
              ? null
              : Number(value)
            : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      if (isEdit) await axiosInstance.put(`${apiPath}/${id}`, formData);
      else await axiosInstance.post(apiPath, formData);
      navigate(listRoute);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string | string[] } } })?.response
        ?.data?.message;
      setError(Array.isArray(msg) ? msg.join(", ") : msg || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const renderField = (f: FieldConfig): ReactNode => {
    const val = formData[f.name];
    if (f.type === "checkbox") {
      return (
        <label
          key={f.name}
          className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 sm:col-span-2 xl:col-span-3"
        >
          <input type="checkbox" name={f.name} checked={Boolean(val)} onChange={handleChange} />
          <span>{f.label}</span>
        </label>
      );
    }
    if (f.type === "select") {
      return (
        <div key={f.name}>
          <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            {f.label}
          </label>
          <select
            name={f.name}
            value={String(val ?? "")}
            onChange={handleChange}
            className="h-11 w-full rounded-lg border border-gray-200 bg-white px-4 text-sm shadow-theme-xs focus:border-brand-300 focus:outline-none focus:ring-2 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            required={f.required}
          >
            <option value="">Select</option>
            {f.options?.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      );
    }
    if (f.type === "textarea") {
      return (
        <div key={f.name} className={adminFormFullWidthClass}>
          <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            {f.label}
          </label>
          <textarea
            name={f.name}
            value={String(val ?? "")}
            onChange={handleChange}
            rows={3}
            required={f.required}
            placeholder={f.placeholder}
            className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm shadow-theme-xs focus:border-brand-300 focus:outline-none focus:ring-2 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
          />
        </div>
      );
    }
    return (
      <InputWithLabel
        key={f.name}
        type={f.type || "text"}
        label={f.label}
        name={f.name}
        value={val === null || val === undefined ? "" : String(val)}
        onChange={handleChange}
        required={f.required}
        placeholder={f.placeholder}
      />
    );
  };

  return (
    <AdminPageShell>
      <PageBreadcrumb
        pageTitle={isEdit ? `Edit ${title}` : `Add ${title}`}
        description={
          isEdit
            ? `Update ${title.toLowerCase()} record`
            : `Create a new ${title.toLowerCase()} record`
        }
      />

      <AdminCard>
        {error && <div className={`mb-4 ${adminAlertErrorClass}`}>{error}</div>}
        <form onSubmit={handleSubmit} className={`${adminFormGridClass} w-full`}>
          {fields.map(renderField)}
          <div className={adminFormFullWidthClass}>
            <AdminFormActions listRoute={listRoute} saving={loading} />
          </div>
        </form>
      </AdminCard>
    </AdminPageShell>
  );
}
